<template>
  <KioskCenteredLayout v-if="isLoading">
    <KioskSpinner/>
  </KioskCenteredLayout>
  <div v-else>
    <KioskMenuHeader
      v-show="isShowHeader($router.currentRoute)"
      :is-menu-header="isShowMenuHeader($router.currentRoute)"
      :class="$style.header"
    />
    <slot />
    <Cart :display="showCart"
          :current-page="$route.name"
          @closeCart="setCartDisplay(false)"
          @checkout="handleCheckout" />

    <!-- Modal when you want to go to menu -->
    <KioskConfirmMessage
      v-if="modalInfo('goToMenuOrMain') === 'menu'"
      title="Abandon boss creation?"
      confirm-text="Yes, abandon"
      cancel-text="No, keep creating"
      @cancel="modalUpdate({ name: 'goToMenuOrMain' })"
      @confirm="redirectToMenu"
    />

    <!-- Modal when you want to go to main -->
    <KioskConfirmMessage
      v-if="modalInfo('cartOrderCancel')"
      title="Cancel your entire order?"
      confirm-text="Yes, cancel"
      cancel-text="No"
      @cancel="modalUpdate({ name: 'cartOrderCancel' })"
      @confirm="cancelOrder"
    />

    <!-- Modal when you want to edit item -->
    <KioskConfirmMessage
      v-if="modalInfo('cartItemEdit')"
      title="Abandon boss creation?"
      confirm-text="Yes, abandon"
      cancel-text="No, keep creating"
      @cancel="modalUpdate({ name: 'cartItemEdit' })"
      @confirm="handleEditItem"
    />

    <!-- Modal when you want to remove item -->
    <KioskConfirmMessage
      v-if="modalInfo('cartItemRemove')"
      title="Remove item from your order?"
      confirm-text="Yes, remove item"
      cancel-text="Keep item"
      @cancel="modalUpdate({ name: 'cartItemRemove' })"
      @confirm="handleRemoveItem"
    />

    <ApiErrors />
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { Action, Getter } from 'vuex-class';
import Cart from '@/apps/cart/components/Cart';
import {
  CART_DISPLAY,
  CART_ITEM_REMOVE,
  CART_RESET,
  CART_PREPARE_ORDER,
  CART_CALC_ORDER,
  ORDER_PATCH,
} from '@/apps/cart/store/types';
import KioskMenuHeader from '@/apps/kiosk/components/KioskMenuHeader';
import KioskConfirmMessage from '@/apps/kiosk/components/KioskConfirmMessage';
import KioskCenteredLayout from '@/layouts/KioskCenteredLayout';
import { MODAL_UPDATE, API_ERROR_SET } from '@/apps/core/store/types';
import { AUTH_LOGOUT, AUTH_GUEST_TOKEN } from '@/apps/auth/store/types';
import { CONFIGURATOR_CLEAR, CONFIGURATOR_PRODUCT } from '@/apps/configurator/store/types';
import ApiErrors from '@/components/ApiErrors';
import { ROUTES_WITHOUT_HEADER, ROUTE_WITH_START_OVER } from '@/apps/kiosk/constants';
import LoadingMixin from '@/lib/mixins/loading-mixin';
import KioskSpinner from '@/apps/kiosk/components/KioskSpinner';
import { MENU_LOAD, LAZY_MENU_LOAD } from '@/apps/menu/store/types';
import { OnIdle } from 'vue-plugin-helper-decorator';

/**
 * Provide layout to kiosk screen. Can be dynamically selected in the default
 * layout component.
 */
@Component({
  components: {
    KioskCenteredLayout,
    KioskSpinner,
    ApiErrors,
    KioskConfirmMessage,
    Cart,
    KioskMenuHeader,
  },
  mixins: [
    LoadingMixin,
  ],
})
export default class KioskLayout extends Vue {
  @Getter showCart;
  @Getter orderItemId;
  @Getter modalInfo;
  @Getter cart;
  @Getter orderItemCompleted;
  @Getter pastOrdersShow;
  @Getter isGuest;
  @Getter guestAccountData;
  @Getter restaurantId;
  @Getter cartIsEmpty;
  @Getter order;
  @Getter calculatedOrder;
  @Getter getPreparedItems;
  @Action(MENU_LOAD) loadMenu;
  @Action(LAZY_MENU_LOAD) loadLazyMenu;
  @Action(MODAL_UPDATE) modalUpdate;
  @Action(CART_DISPLAY) cartDisplay;
  @Action(CART_ITEM_REMOVE) cartRemoveItem;
  @Action(CART_RESET) cartReset;
  @Action(CONFIGURATOR_PRODUCT) setCurrentProduct;
  @Action(CART_PREPARE_ORDER) prepareOrder;
  @Action(ORDER_PATCH) updateOrder;
  @Action(CART_ITEM_REMOVE) removeOrderItem;
  @Action(CONFIGURATOR_CLEAR) clearConfigurator;
  @Action(AUTH_LOGOUT) logout;
  @Action(AUTH_LOGOUT) authLogout;
  @Action(CART_CALC_ORDER) cartCalcOrder;
  @Action(API_ERROR_SET) setApiError;
  @Action(AUTH_GUEST_TOKEN) getNewGuestToken;

  @OnIdle()
  async logOut() {
    await this.authLogout();
    this.$router.push({ path: '/' });
  }

  async mounted() {
    await this.loadMenu({ restaurantId: this.restaurantId });
    await this.loadLazyMenu({ restaurantId: this.restaurantId });
  }

  setCartDisplay(value) {
    this.cartDisplay(value);
  }

  /**
   * Check that need to show header
   * @param currentRoute
   * @return {boolean}
   */
  isShowHeader(currentRoute) {
    return !ROUTES_WITHOUT_HEADER.includes(currentRoute.name);
  }

  /**
   * Check that display menu header with start over button.
   * @param currentRoute
   * @returns {boolean}
   */
  isShowMenuHeader(currentRoute) {
    return ROUTE_WITH_START_OVER === currentRoute.name && !this.pastOrdersShow;
  }

  async handleCheckout(payload) {
    const data = this.isGuest ? this.guestAccountData : null;

    this.loadingStart();

    try {
      if (payload.promoCode) {
        await this.updateOrder({
          userData:  data,
          promoCode: payload.promoCode,
          items:     this.getPreparedItems,
        });
      } else {
        await this.prepareOrder({
          userData:  data,
          promoCode: payload.promoCode,
        });
      }
      this.$router.push({ name: 'payment' });
    } catch (error) {
      this.setApiError({ detail: error });
      throw new Error(error);
    } finally {
      this.loadingStop();
    }
  }

  redirectToMenu() {
    const nextRoute = this.modalInfo('goToMenuOrMain');

    if (nextRoute === 'main') {
      this.cartReset();
    }

    this.modalUpdate({ name: 'goToMenuOrMain' });
    this.$router.push({ name: nextRoute });
  }

  cancelOrder() {
    this.modalUpdate({ name: 'cartOrderCancel' });
    this.logout();
    this.$router.push({ name: 'main' });
  }

  handleEditItem() {
    const itemId = this.modalInfo('cartItemEdit');
    const item = this.cart[itemId];

    if (!this.orderItemCompleted) {
      this.removeOrderItem(this.orderItemId);
    }

    this.clearConfigurator();

    this.setCurrentProduct({
      productKey:  item.productKey,
      orderItemId: itemId,
      orderItem:   item.data,
    });

    this.modalUpdate({ name: 'cartItemEdit' });
    this.cartDisplay(false);
    this.$router.push({ name: 'configurator', params: { id: item.productKey } });
  }

  handleRemoveItem() {
    const itemId = this.modalInfo('cartItemRemove');

    if (this.order) {
      const removedItem = this.order.items.find(item => item.id === itemId);

      if (this.calculatedOrder.totalPrice - removedItem.price <= 0) {
        this.setApiError({ detail: 'You can not remove this item. The total price is less than your promocode.' });

        return;
      }
    }

    this.cartRemoveItem(itemId);

    if (itemId === this.orderItemId) {
      this.$router.push({ name: 'menu' });
    }

    this.modalUpdate({ name: 'cartItemRemove' });

    if (this.cartIsEmpty) {
      this.cartReset();
      this.cartDisplay(false);

      if (this.isGuest) {
        this.getNewGuestToken();
      }
    } else {
      const promoCode = this.order ? this.order.promotionCodeApplied : undefined;

      this.cartCalcOrder({ promoCode });
    }
  }
}
</script>

<style lang="scss" module>
.header {
  z-index: 10;
}
</style>



// WEBPACK FOOTER //
// src/layouts/KioskLayout.vue